import styled from "@emotion/styled/macro";
import { Box } from "@chakra-ui/react";
import { colors } from "../../../styles/theme";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import plate from "../../../assets/images/plate.svg";
import calendar from "../../../assets/images/calendar.svg";
import { Field } from "formik";
import { withProps } from "recompose";
import DatePickerField from "../../../components/ui/DatePickerField";
import { addMonths } from "date-fns";
import Hint from "../../../components/ui/Hint";
import { VscTriangleRight, VscTriangleDown } from "react-icons/vsc";
import SelectField from "../../../components/ui/SelectField";
import * as yup from "yup";

const LicenseInput = styled.div`
  display: flex;
  flex-direction: column;
`;

const PlateWrapper = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 8em;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 90%;
  }
  width: 7em;
  height: 1.5em;
  color: ${({ theme }) => theme.colors.red};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  border: 3px solid ${({ theme }) => theme.colors.red};
  border-radius: 7px;
  display: flex;
`;
const PlateInput = styled(Field)`
  height: 100% !important;
  width: 100%;
  min-width: 0px;
  outline: 0px;
  font-size: ${({ theme }) => theme.fontSizes.md};
  background: inherit;
  border: 0px;
  border-radius: 0px;
  padding-left: 0.25em;
  text-transform: uppercase;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-top: 6px;
  }
  ::placeholder {
    color: #e1095166;
    font-weight: bold;
    font-size: 0.75em;
    text-align: center;
    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: 1em;
    }
  }
  &:focus {
    box-shadow: none;
  }
  &:focus::placeholder {
    color: transparent;
  }

  input:-internal-autofill-selected {
    color: #fff !important;
  }
`;
const LicenseInputWrapper = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-family: ${({ theme }) => theme.fonts.body};
  border: 0;
  display: flex;
`;
const DateInput = styled.div`
  margin: 1em 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin: 0;
    width: 30%;
  }
`;

const IconPlateWrapper = styled.div`
  display: flex;
  width: 15%;
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 16%;
  }
`;
const DateWrapper = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.25em;
  }
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 5px;
  display: flex;
  height: 2.5em;
`;
const IconWrapper = styled.div`
  border-right: 1px solid ${({ theme }) => theme.colors.primary};
  width: 25%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const DatePickerWrapper = styled.div``;

const SmallLabel = styled.p`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.secondary};
  line-height: 1.2em;
  padding-top: 0.5em;
`;

const Label = styled.p`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: underline;
  width: fit-content;
`;

const VinInput = styled(Field)`
  border: 1px solid ${({ theme }) => theme.colors.primary};
  height: 2.5em;
  min-width: 0px;
  outline: 0px;
  position: relative;
  appearance: none;
  transition: all 0.2s ease 0s;
  text-transform: uppercase;
  font-size: ${({ theme }) => theme.fontSizes.md};
  padding-inline-start: var(--chakra-space-4);
  padding-inline-end: var(--chakra-space-4);
  border-radius: var(--chakra-radii-md);
  background: inherit;
  border: 1px solid rgb(50, 81, 70);
  height: 2.5em;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 35%;
  }

  &:focus {
    box-shadow: none;
  }
`;
const LabelContainer = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    align-items: center;
  }
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: baseline;
  margin-bottom: 0.5em;

  svg {
    width: 15px;
    height: 15px;
  }
`;

const FieldWrapper = styled.div`
  margin-top: 1.5em;
`;

const HintWrapper = styled.div`
  display: inline-block;
  font-weight: normal;
  user-select: none;
  margin-left: 1em;
`;

const Plate = withProps({ src: plate })(styled.img`
  position: absolute;
  height: 1.35em;
  border-bottom-left-radius: 5px;
  border-top-left-radius: 5px;
`);
const CalendarIcon = withProps({ src: calendar })(styled.img`
  position: absolute;
`);

// eslint-disable-next-line
export const validationSchema = (t) => {
  return yup.object().shape(
    {
      vin: yup.string().when("licensePlate", {
        is: (val) => !!val,
        then: (schema) => yup.string(),
        otherwise: (schema) => yup.string().required(t("common.LicenseOrVinError")),
      }),
      licensePlate: yup.string().when("vin", {
        is: (val) => !!val,
        then: (schema) => yup.string(),
        otherwise: (schema) => yup.string().required(t("common.LicenseOrVinError")),
      }),
      legitimateInterest: yup.string().required(t("common.legitimateInterestError")),
      terms: yup.bool().required(t("common.termsError")),
    },
    [["licensePlate", "vin", "legitimateInterest", "terms"]]
  );
};

export const legitimateInterestsOptions = (t) => [
  {
    value: "",
    label: t("insuranceCheck.legitimateInterest.placeholder"),
  },
  {
    value: "AGGRIEVED_PARTY_OR_PARTY_ACTING_ON_BEHALF_OF",
    label: t("insuranceCheck.legitimateInterest.AGGRIEVED_PARTY_OR_PARTY_ACTING_ON_BEHALF_OF"),
  },
  {
    value: "VEHICLE_OWNER_OR_PARTY_ACTING_ON_BEHALF_OF",
    label: t("insuranceCheck.legitimateInterest.VEHICLE_OWNER_OR_PARTY_ACTING_ON_BEHALF_OF"),
  },
  {
    value: "VEHICLE_DRIVER_OR_PARTY_ACTING_ON_BEHALF_OF",
    label: t("insuranceCheck.legitimateInterest.VEHICLE_DRIVER_OR_PARTY_ACTING_ON_BEHALF_OF"),
  },
  {
    value: "VEHICLE_HOLDER_OR_PARTY_ACTING_ON_BEHALF_OF",
    label: t("insuranceCheck.legitimateInterest.VEHICLE_HOLDER_OR_PARTY_ACTING_ON_BEHALF_OF"),
  },
  {
    value: "PARTY_AUTHORIZED_BY_LAW_DECREE_ARRANGEMENT",
    label: t("insuranceCheck.legitimateInterest.PARTY_AUTHORIZED_BY_LAW_DECREE_ARRANGEMENT"),
  },
];

const DataCheckbox = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.primary};
  input {
    border: 1px solid ${({ theme }) => theme.colors.primary};
    margin-right: 1em;
    &:checked {
      background: ${({ theme }) => theme.colors.primary};
    }
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-right: 15em;
  }
  align-items: flex-start;
  line-height: 1em;
  margin-top: 1em;
  & a {
    text-decoration: underline;
  }
`;

export const Checkbox = ({ name, children, error }) => {
  return (
    <DataCheckbox>
      <label>
        <Field name={name} type="checkbox" />
        {children}
      </label>
      {error && (
        <Box mt={2} color={colors.red} fontSize="sm">
          {error}
        </Box>
      )}
    </DataCheckbox>
  );
};

export const LicensePlateInputField = ({ togglePlate = true, values, toggleVinAndPlate, errors }) => {
  const { t } = useTranslation();

  return (
    <FieldWrapper>
      <LabelContainer onClick={toggleVinAndPlate}>
        {togglePlate ? <VscTriangleDown /> : <VscTriangleRight />}
        <Label>{t("insuranceCheck.license")}</Label>
      </LabelContainer>
      {togglePlate && (
        <LicenseInput>
          <PlateWrapper>
            <IconPlateWrapper>
              <Plate />
            </IconPlateWrapper>

            <LicenseInputWrapper>
              <PlateInput
                id="licensePlate"
                name="licensePlate"
                placeholder={t("insuranceCheck.platePlaceholder")}
                maxLength={10}
                disabled={values.vin !== ""}
              />
            </LicenseInputWrapper>
          </PlateWrapper>
          <Box mt={2} color={colors.red} fontSize="sm">
            {errors?.licensePlate}
          </Box>
          <SmallLabel>{t("insuranceCheck.licenseInput")}</SmallLabel>
        </LicenseInput>
      )}
    </FieldWrapper>
  );
};

export const VinInputField = ({ toggleVinAndPlate, isVin, values }) => {
  const { t } = useTranslation();

  return (
    <FieldWrapper>
      <LabelContainer onClick={toggleVinAndPlate}>
        {isVin ? <VscTriangleDown /> : <VscTriangleRight />}
        <Label>{t("insuranceCheck.vinTitle")}</Label>
        <HintWrapper>
          <Hint title={t("insuranceCheck.vinTitle")}>{t("insuranceCheck.vinHint")}</Hint>
        </HintWrapper>
      </LabelContainer>

      {isVin && (
        <LicenseInput>
          <VinInput id="vin" name="vin" maxLength={17} disabled={values.licensePlate !== ""} />
          <SmallLabel>{t("insuranceCheck.vinLabel")}</SmallLabel>
        </LicenseInput>
      )}
    </FieldWrapper>
  );
};

export const DateInputField = ({ isDate, toggleDate }) => {
  const { t } = useTranslation();

  const minLoadDate = addMonths(new Date(), -window._env_?.REACT_APP_POI_QUERY_MAX_PAST_MONTHS);
  const maxLoadDate = addMonths(new Date(), window._env_?.REACT_APP_POI_QUERY_MAX_FUTURE_MONTHS);

  return (
    <FieldWrapper>
      <LabelContainer onClick={toggleDate}>
        {isDate ? <VscTriangleDown /> : <VscTriangleRight />}
        <Label>{t("insuranceCheck.statusDate")}</Label>
        <HintWrapper>
          <Hint title={t("insuranceCheck.statusDate")}>{t("insuranceCheck.dateHint")}</Hint>
        </HintWrapper>
      </LabelContainer>
      {isDate && (
        <DateInput>
          <DateWrapper>
            <IconWrapper>
              <CalendarIcon />
            </IconWrapper>
            <DatePickerWrapper>
              <DatePickerField
                name="date"
                label={t("insuranceCheck.statusDate")}
                minDate={minLoadDate}
                maxDate={maxLoadDate}
                locale="en"
              />
            </DatePickerWrapper>
          </DateWrapper>
        </DateInput>
      )}
    </FieldWrapper>
  );
};

export const LegitimateInterestField = () => {
  const { t } = useTranslation();
  return (
    <FieldWrapper>
      <Label>{t("insuranceCheck.legitimateInterest.label")}</Label>
      <SelectField name="legitimateInterest" options={legitimateInterestsOptions(t)} />
    </FieldWrapper>
  );
};

export const ConsentCheckbox = ({ errors, values }) => {
  return (
    <Checkbox name="terms" error={errors?.terms}>
      <Trans i18nKey="insuranceCheck.dataProtection">
        <Link
          to={{
            pathname: "/privacy",
            state: values,
          }}
        />
        i agree to the
        <Link
          to={{
            pathname: "/terms",
            state: values,
          }}
        />
      </Trans>
    </Checkbox>
  );
};
